import {
  AdvancedLogicOperators,
  AdvancedLogicTypes,
  AdvancedLogicActions,
} from '@/utils/constants';

/**
 * Return a result according the current value and the rule value.
 * @param args
 */
const advancedLogicConditionStrategy = (args: any) => {
  const { currValue, value, type } = args;

  switch (type) {
    case AdvancedLogicTypes.Is:
      return currValue === value;

    case AdvancedLogicTypes.IsNot:
      return currValue !== value;

    case AdvancedLogicTypes.IsEmpty:
      return currValue === null || currValue?.length === 0;

    case AdvancedLogicTypes.IsNotEmpty:
      return currValue?.length > 0;

    case AdvancedLogicTypes.StartsWith:
      return RegExp(`^${value}`).test(currValue);

    case AdvancedLogicTypes.NotStartsWith:
      return !RegExp(`^${value}`).test(currValue);

    case AdvancedLogicTypes.Contains:
      return currValue.includes(value);

    case AdvancedLogicTypes.NotContains:
      return !currValue.includes(value);

    default:
      throw new Error(`AdvancedLogicConditionType "${type}" not defined`);
  }
};

const advancedLogicConditionsResolver = (conditions: any[], value: any) => {
  return conditions.map((condition: any) => ({
    operator: condition.operator,
    result: advancedLogicConditionStrategy({
      type: condition.type,
      value: condition?.value?.toUpperCase(),
      currValue: value[condition.field_id]?.toUpperCase() || '',
    }),
  }));
};

const advancedLogicConditionsResult = (conditions: any[]) => {
  const conditionsReduced = conditions.reduce((acc, curr) => {
    // It only occurs if there is only one condition
    if (curr.operator === null) {
      return acc.result;
    }

    if (curr.operator === AdvancedLogicOperators.And) {
      return acc.result && curr.result;
    }

    // AdvancedLogicOperators.Or;
    return acc.result || curr.result;
  });

  // conditionsReduced?.result in case it's only one condition.
  return conditionsReduced?.result ?? conditionsReduced;
};

const advancedLogicRulesResolver = (rules: any[], value: any) => {
  return rules.map(({ action, field_id: fieldId, conditions }) => {
    const conditionsResolved = advancedLogicConditionsResolver(conditions, value);
    const conditionsResult = advancedLogicConditionsResult(conditionsResolved);

    return {
      action,
      fieldId,
      result: conditionsResult,
    };
  });
};

/**
 * Returns an array of fields that needs to be hidden.
 * @param rules
 */
const advancedLogicFieldsHidden = (rulesMapped: any[]) => {
  return rulesMapped.reduce((acc, curr) => {
    if (curr.action === AdvancedLogicActions.Show && curr.result) {
      return [...acc];
    }

    if (curr.action === AdvancedLogicActions.Hide && !curr.result) {
      return [...acc];
    }

    // AlwaysShow?

    return [...acc, curr.fieldId];
  }, []);
};

export const advancedLogicFacade = ({ value, advancedRules }: any) => {
  const rulesResolved = advancedLogicRulesResolver(advancedRules, value);
  const fieldsHidden = advancedLogicFieldsHidden(rulesResolved);

  return fieldsHidden;
};

export const advancedLogicFilterFields = ({ ids, controls }: any) => {
  // If there are no elements to hide then return original controls.
  if (!ids.length) {
    return controls;
  }

  return controls.map((row: any) =>
    row.filter((col: any) => {
      if (Array.isArray(col)) {
        throw new Error('3rd level of array elements is not supported');
      }

      // If the form control is "select", attributes are retrieved differently.
      if (col.type === 'options') {
        return !ids.find((id: string) => `${id}_2` === col.options[0].attributes.id);
      }

      // Rest of controls behave in the same way.
      return col?.attributes?.id
        ? !ids.find((id: string) => `${id}_2` === col.attributes.id)
        : true;
    }),
  );
};

export const advancedLogicEmptyFieldsValue = (ids: string[]) => {
  return ids.reduce((acc, curr) => ({ ...acc, [curr]: '' }), {});
};
